import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import { BehaviorSubject, of, ReplaySubject, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import Credentials from '../../interfaces/credentials.interface';
import SignUpUser from '../../interfaces/signup-user.interface';
import User from '../../interfaces/user.interface';
import { backend, CustomError, i18n, validation } from '../../utils';
import { authState$ } from '../../utils/backend/backend.util';
import { getCurrentLanguage } from '../../utils/i18n/i18n.util';

/**
 * The account$ subject contains the currently authenticated user (account).
 * This field is generally not intended to be used directly.
 */
const account$ = new ReplaySubject<User | undefined>(1);

/**
 * Get the current user.
 *
 * @example
 *  user$.subscribe(...);
 *  const { value: user, error, isLoading } = useObservable(user$, []);
 */
export const user$ = account$.asObservable();

/**
 * The lang$ subject contains the currently active language.
 * This field is generally not intended to be used directly.
 */
const lang$ = new BehaviorSubject<string>(getCurrentLanguage());

/**
 * Get the currently active language.
 *
 * @example
 *  lang$.subscribe(...);
 *  const { value: language, error, isLoading } = useObservable(lang$);
 */
export const language$ = lang$.asObservable().pipe(distinctUntilChanged());

/** Synchronously determine whether the firebase connection is initialized. */
export const { isInitialized } = backend;

/**
 * Login into an existing account.
 *
 * @example
 *  await login$({ email: 'mail@example.org', password: 'secret123' });
 */
export const login$ = async ({ email, password }: Credentials): Promise<void> => {
  await backend.isInitialized$;

  try {
    await firebase.auth().signInWithEmailAndPassword(email, password);
  } catch (error) {
    switch (error.code) {
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        throw new CustomError({
          ...CustomError.errors.user.AuthInvalidLogin,
          originalError: error,
        });

      case 'auth/user-disabled':
        throw new CustomError({
          ...CustomError.errors.user.AuthAccountDisabled,
          originalError: error,
        });

      default:
        throw new CustomError({ ...CustomError.errors.backend.AuthSignIn, originalError: error });
    }
  }
};

/**
 * Log out (current user).
 *
 * @example
 *  await logout$();
 */
export const logout$ = async (): Promise<void> => {
  await backend.isInitialized$;

  try {
    await firebase.auth().signOut();
  } catch (error) {
    throw new CustomError({
      ...CustomError.errors.backend.AuthSignOut,
      originalError: error,
    });
  }
};

/**
 * Create a new user & send a verification mail.
 *
 * @example
 *  await signUp$({
 *    name: 'Max Muster',
 *    email: 'mail@example.org',
 *    password: 'secret123'
 *  });
 */
export const signUp$ = async ({ name, email, password, language }: SignUpUser): Promise<void> => {
  await backend.isInitialized$;

  try {
    validation.signUpUser.validateSync({ name, email, password, language });
  } catch (err) {
    throw new CustomError({
      ...CustomError.errors.user.DataInvalidSignUpData,
      originalError: err,
    });
  }

  try {
    const auth = firebase.auth();

    auth.languageCode = language;

    await auth.createUserWithEmailAndPassword(email, password);

    const { currentUser } = auth;

    await currentUser?.updateProfile({ displayName: name });

    await saveUser$(
      {
        uid: currentUser?.uid as string,
        name: name as string,
        email,
        language,
        isVerified: false,
      },
      true
    );

    await sendVerificationMail$();
  } catch (error) {
    switch (error.code) {
      default:
        throw new CustomError({ ...CustomError.errors.backend.AuthSignUp, originalError: error });

      case 'auth/weak-password':
        throw new CustomError({
          ...CustomError.errors.user.AuthWeakPassword,
          originalError: error,
        });

      case 'auth/email-already-in-use':
        throw new CustomError({
          ...CustomError.errors.user.AuthUserAlreadyExists,
          originalError: error,
        });
    }
  }
};

/**
 * Send the verification mail to the currently authenticated user.
 *
 * @example
 *  await sendVerificationMail$();
 */
export const sendVerificationMail$ = async (): Promise<void> => {
  await backend.isInitialized$;

  const { currentUser } = firebase.auth();

  if (!currentUser) {
    throw new CustomError({ ...CustomError.errors.user.AuthUnauthorized });
  }

  try {
    await currentUser.sendEmailVerification();
  } catch (error) {
    throw new CustomError({
      ...CustomError.errors.backend.AuthVerification,
      originalError: error,
    });
  }
};

/**
 * Change the current user's password.
 *
 * @example
 *  await changePassword$('Sup3rS3(r3tP@$$w0rD');
 */
export const changePassword$ = async (password: string): Promise<void> => {
  await backend.isInitialized$;

  try {
    validation.password.validateSync(password);
  } catch (err) {
    throw new CustomError({
      ...CustomError.errors.user.DataInvalidPassword,
      originalError: err,
    });
  }

  const { currentUser } = firebase.auth();

  if (!currentUser) {
    throw new CustomError({ ...CustomError.errors.user.AuthUnauthorized });
  }

  try {
    return await currentUser.updatePassword(password);
  } catch (error) {
    throw new CustomError({
      ...CustomError.errors.backend.AuthPasswordUpdate,
      originalError: error,
    });
  }
};

/**
 * Save a given user.
 *
 * @example
 *  await saveUser$({
 *    uid: 'R1rXq1PCOQYP9R00u4MHQ1o2eg31',
 *    name: 'Max Muster',
 *    email: 'mail@example.org',
 *    language: 'de',
 *  });
 */
export const saveUser$ = async (user: User, create = false): Promise<void> => {
  await backend.isInitialized$;

  try {
    validation.user.validateSync(user);
  } catch (err) {
    throw new CustomError({
      ...CustomError.errors.user.DataInvalidAccountData,
      originalError: err,
    });
  }

  const { currentUser } = firebase.auth();

  if (!currentUser) {
    throw new CustomError({ ...CustomError.errors.user.AuthUnauthorized });
  }

  // Keep the firebase user's displayName in sync
  if (user.name !== currentUser.displayName) {
    currentUser.updateProfile({ displayName: user.name });
  }

  await backend.saveDocument$<User>({
    collection: 'users',
    doc: user.uid,
    data: user,
    ...(create
      ? {}
      : { options: { mergeFields: ['name', 'language', 'isVerified', 'updatedAt'] } }),
  });
};

/**
 * Initialize the service (this is done automatically)
 */
const init = (): Subscription => {
  /** Set the firebase user's language to the account's language */
  const setLanguage = ({ language }: User): void => {
    const lang = language || i18n.getCurrentLanguage();
    firebase.auth().languageCode = lang;
    lang$.next(lang);
  };

  /**
   * Ensure that the verification state is kept in sync between the firebase user
   * and the account data stored within the database.
   */
  const postProcessVerification = (user: firebase.User) => (account: User): void => {
    if (user.emailVerified && !account.isVerified) {
      saveUser$({ ...account, isVerified: true });

      // We need to force a token refresh after the verification process.
      // Otherwise the creation of new documents within the database will be
      // rejected by google.
      user.getIdToken(true);
    }
  };

  /** Firebase listener to react to authState changes (login/logout). */
  return authState$
    .pipe(
      catchError((error) => {
        // Logout:
        account$.next(undefined);

        return throwError(
          error instanceof CustomError
            ? error
            : new CustomError({
                ...CustomError.errors.backend.Auth,
                originalError: { ...error, name: error.code },
              })
        );
      }),
      switchMap((user) => {
        if (!user) {
          return of(undefined);
        }

        const accountData$ = backend.queryDocument$<User>({
          collection: 'users',
          doc: user.uid,
          isRequired: false,
        });

        return accountData$.pipe(
          tap(postProcessVerification(user)),
          tap(setLanguage),
          map((account) => ({ ...account, isVerified: user.emailVerified }))
        );
      })
    )
    .subscribe(account$);
};

// Auto init
init();

/** Some useful exports for testing purposes only */
export const testing = {
  account$,
  init,
  lang$,
};
